import { FailureFilters, DEFAULT_FAILURE_FILTERS } from '@/types/filters';
import { vblApi } from './vblApi';
import { turbineWorkspaceMappingsApi } from './turbineWorkspaceMappings';
import { workspacesApi } from './workspacesApi';
import type {
  VblForecastProject,
  SaveVblForecastRequest
} from '@/types/vblForecast';
import type { TurbineToForecast, VblAnalysisResult } from '@/types/vbl';
import type { TurbineWorkspaceMapping } from '@/types/turbineWorkspaceMapping';

/**
 * VBL Forecast API Service
 * Handles VBL forecasting operations including turbine-to-forecast retrieval,
 * workspace mapping integration, and forecast calculations
 */
class VblForecastApiService {
  /**
   * Get turbines that need VBL forecasting
   * These are turbines with detected failures but no confirmed failure yet
   */
  async getTurbinesToForecast(filters: FailureFilters = DEFAULT_FAILURE_FILTERS): Promise<TurbineToForecast[]> {
    try {
      // Override pagination to get ALL turbines, not just first page
      const modifiedFilters = {
        ...filters,
        size: 10000,  // Large enough to get all turbines
        page: 1
      };

      const response = await vblApi.getTurbinesToForecast(modifiedFilters);
      return response.turbines;
    } catch (error) {
      console.error('Failed to fetch turbines to forecast:', error);
      return [];
    }
  }

  /**
   * Get workspace mappings for specific turbines
   * Used to find which VBL analysis workspaces are mapped to each turbine-component
   */
  async getTurbineWorkspaceMappings(turbineIds: string[]): Promise<TurbineWorkspaceMapping[]> {
    try {

      // Fetch ALL mappings with a large page size to avoid pagination issues
      // The backend doesn't properly filter by turbine_id, so we need to get all and filter client-side
      const response = await turbineWorkspaceMappingsApi.getMappings({
        page: 1,
        size: 10000  // Large enough to get all mappings in one request
      });


      // Filter for the turbines we actually need
      const relevantMappings = response.mappings.filter(m =>
        turbineIds.includes(m.turbine_id)
      );


      // Log details for debugging
      turbineIds.forEach(turbineId => {
        const _turbineMappings = relevantMappings.filter(m => m.turbine_id === turbineId);
      });

      return relevantMappings;
    } catch (error) {
      console.error('Failed to fetch turbine workspace mappings:', error);
      return [];
    }
  }

  /**
   * Get VBL analysis parameters for a specific workspace
   * Returns the Weibull parameters needed for forecasting
   */
  async getVblAnalysisParameters(workspaceId: string): Promise<VblForecastProject['vblParameters'] | null> {
    try {
      const response = await workspacesApi.load(workspaceId, undefined, undefined, 'vbl');
      const workspace = response.workspace;

      if (!workspace.results) {
        console.warn(`No VBL analysis results found for workspace ${workspaceId}`);
        return null;
      }

      const vblResults = workspace.results as unknown as VblAnalysisResult;

      return {
        shape: vblResults.parameters.shape,
        scale: vblResults.parameters.scale,
        shapeCI: vblResults.parameters.shapeCI,
        scaleCI: vblResults.parameters.scaleCI,
        rSquared: vblResults.parameters.rSquared || 0,
        standardErrors: vblResults.statistics?.standardErrors
      };
    } catch (error) {
      console.error(`Failed to get VBL parameters for workspace ${workspaceId}:`, error);
      return null;
    }
  }

  /**
   * Get comprehensive forecast data for turbines
   * Combines turbines to forecast with their workspace mappings and VBL parameters
   */
  async getVblForecastData(filters: FailureFilters = DEFAULT_FAILURE_FILTERS): Promise<{
    turbinesToForecast: TurbineToForecast[];
    mappings: TurbineWorkspaceMapping[];
    workspaceParameters: Record<string, VblForecastProject['vblParameters']>;
  }> {
    try {
      // Get turbines that need forecasting
      const turbinesToForecast = await this.getTurbinesToForecast(filters);

      if (turbinesToForecast.length === 0) {
        return {
          turbinesToForecast: [],
          mappings: [],
          workspaceParameters: {}
        };
      }

      // Get workspace mappings for these turbines
      const turbineIds = turbinesToForecast.map(t => t.turbine_id);
      const mappings = await this.getTurbineWorkspaceMappings(turbineIds);

      // Get VBL parameters for each mapped workspace
      const workspaceIds = Array.from(new Set(mappings.map(m => m.workspace_id)));
      const workspaceParameters: Record<string, VblForecastProject['vblParameters']> = {};

      for (const workspaceId of workspaceIds) {
        const parameters = await this.getVblAnalysisParameters(workspaceId);
        if (parameters) {
          workspaceParameters[workspaceId] = parameters;
        }
      }


      return {
        turbinesToForecast,
        mappings,
        workspaceParameters
      };
    } catch (error) {
      console.error('Failed to get VBL forecast data:', error);
      return {
        turbinesToForecast: [],
        mappings: [],
        workspaceParameters: {}
      };
    }
  }

  /**
   * Calculate current degradation time for a turbine
   * Based on days since detection date
   */
  calculateCurrentDegradationTime(turbine: TurbineToForecast): number {
    try {
      const detectionDate = new Date(turbine.detected_date);
      const currentDate = new Date();
      const timeDiff = currentDate.getTime() - detectionDate.getTime();
      return Math.max(0, Math.floor(timeDiff / (1000 * 60 * 60 * 24))); // Days
    } catch (error) {
      console.warn(`Failed to calculate degradation time for turbine ${turbine.turbine_id}:`, error);
      return 0;
    }
  }

  /**
   * Save VBL forecast workspace
   */
  async saveForecast(forecast: VblForecastProject, userId: string): Promise<VblForecastProject> {
    try {
      const _request: SaveVblForecastRequest = {
        workspace_name: forecast.name,
        description: forecast.description,
        tags: forecast.metadata.tags,
        owner: userId,
        workspace_type: 'vbl_forecast',
        configuration: forecast.configuration,
        vbl_parameters: forecast.vblParameters,
        turbines_to_forecast: forecast.turbinesToForecast,
        current_results: forecast.results || []
      };

      // For now, store in localStorage (similar to mock workspace API)
      // In production, this would call a backend endpoint
      const savedForecast: VblForecastProject = {
        ...forecast,
        id: `vbl_forecast_${Date.now()}`,
        metadata: {
          ...forecast.metadata,
          userId,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          version: 1
        }
      };

      // Store in localStorage for now
      const existingForecasts = this.getStoredForecasts();
      existingForecasts.push(savedForecast);
      localStorage.setItem('vbl_forecasts', JSON.stringify(existingForecasts));

      return savedForecast;
    } catch (error) {
      console.error('Failed to save VBL forecast:', error);
      throw error;
    }
  }

  /**
   * Load VBL forecast workspace by ID
   */
  async loadForecast(id: string): Promise<VblForecastProject | null> {
    try {
      const forecasts = this.getStoredForecasts();
      return forecasts.find(f => f.id === id) || null;
    } catch (error) {
      console.error(`Failed to load VBL forecast ${id}:`, error);
      return null;
    }
  }

  /**
   * List all VBL forecast workspaces
   */
  async listForecasts(): Promise<VblForecastProject[]> {
    try {
      return this.getStoredForecasts();
    } catch (error) {
      console.error('Failed to list VBL forecasts:', error);
      return [];
    }
  }

  /**
   * Delete VBL forecast workspace
   */
  async deleteForecast(id: string): Promise<void> {
    try {
      const forecasts = this.getStoredForecasts();
      const filtered = forecasts.filter(f => f.id !== id);
      localStorage.setItem('vbl_forecasts', JSON.stringify(filtered));
    } catch (error) {
      console.error(`Failed to delete VBL forecast ${id}:`, error);
      throw error;
    }
  }

  /**
   * Update VBL forecast workspace
   */
  async updateForecast(id: string, updates: Partial<VblForecastProject>, _userId: string): Promise<VblForecastProject> {
    try {
      const forecasts = this.getStoredForecasts();
      const forecastIndex = forecasts.findIndex(f => f.id === id);

      if (forecastIndex === -1) {
        throw new Error(`VBL forecast ${id} not found`);
      }

      const updatedForecast: VblForecastProject = {
        ...forecasts[forecastIndex],
        ...updates,
        metadata: {
          ...forecasts[forecastIndex].metadata,
          ...updates.metadata,
          updatedAt: new Date().toISOString(),
          version: forecasts[forecastIndex].metadata.version + 1
        }
      };

      forecasts[forecastIndex] = updatedForecast;
      localStorage.setItem('vbl_forecasts', JSON.stringify(forecasts));

      return updatedForecast;
    } catch (error) {
      console.error(`Failed to update VBL forecast ${id}:`, error);
      throw error;
    }
  }

  /**
   * Helper method to get stored forecasts from localStorage
   */
  private getStoredForecasts(): VblForecastProject[] {
    try {
      const stored = localStorage.getItem('vbl_forecasts');
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.warn('Failed to parse stored VBL forecasts:', error);
      return [];
    }
  }
}

// Export singleton instance
export const vblForecastApi = new VblForecastApiService();